/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
'use client';

/**
 * MapPickerSheet — lớp lắp ráp cho HOME: mở Sheet (mobile) / Dialog (desktop)
 * toàn màn chứa map chọn địa chỉ bằng cách kéo map (pin cố định giữa).
 *
 * Kiến trúc (theo spec grill-me):
 *  - Pin CỐ ĐỊNH ở tâm (CenterPin), user kéo map → moveEnd → reverse-geocode
 *    tâm (useReverseGeocodeOnIdle).
 *  - Ô search nổi (MapAddressSearch) + FAB về vị trí GPS → flyTo; moveEnd sau
 *    flyTo tự cập nhật lại địa chỉ.
 *  - Bấm "Xác nhận" → gọi `onConfirm(address)` (cha truyền handleAddressSelect
 *    sẵn có của StoreLocation → resolve city + ghi store + điều hướng).
 *  - Map lazy-load + mounted guard để tránh SSR maplibre.
 */
import { Button } from '@ahafood/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@ahafood/ui/components/dialog';
import { Sheet, SheetContent, SheetTitle } from '@ahafood/ui/components/sheet';
import type { Address } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, LocateFixed, MapPin } from 'lucide-react';
import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { MapRef } from 'react-map-gl/maplibre';

import CenterPin from './center-pin';
import MapAddressSearch from './map-address-search';
import type { MapCoords } from './map-view';
import { useReverseGeocodeOnIdle } from './use-reverse-geocode-on-idle';
import { useIsMobile } from '@/lib/is-mobile/use-is-mobile';

const LazyMapView = lazy(() => import('./map-view'));

/** Ahamove HQ (Q10, HCM) — fallback khi không có toạ độ ngữ cảnh. */
export const DEFAULT_MAP_CENTER: MapCoords = {
  longitude: 106.6635045,
  latitude: 10.7693434,
};

const PICK_ZOOM = 16;

export type MapPickerSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Toạ độ tâm khởi đầu (cha resolve: địa chỉ đang chọn → GPS → city center). */
  initialCenter?: MapCoords;
  /** Địa chỉ đang chọn (nếu vào từ "Ghim chính xác") để hiện ngay, khỏi reverse. */
  initialAddress?: Address | null;
  /** Tái dùng handleAddressSelect của StoreLocation. */
  onConfirm: (address: Address) => void | Promise<void>;
  title?: string;
};

function MapSkeleton() {
  return (
    <div className="flex size-full items-center justify-center bg-muted">
      <div className="flex gap-1.5">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="size-2.5 animate-pulse rounded-full bg-muted-foreground/40"
            style={{ animationDelay: `${i * 0.15}s` }}
          />
        ))}
      </div>
    </div>
  );
}

function MapPickerContent({
  onOpenChange,
  initialCenter = DEFAULT_MAP_CENTER,
  initialAddress = null,
  onConfirm,
}: Omit<MapPickerSheetProps, 'open'>) {
  const mapRef = useRef<MapRef | null>(null);
  const [mounted, setMounted] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [center] = useState<MapCoords>(initialCenter);

  const { address, isMoving, isLoading, onMoveStart, onMoveEnd, reverse } =
    useReverseGeocodeOnIdle(initialAddress);

  useEffect(() => {
    setMounted(true);
  }, []);

  const flyTo = useCallback((coords: MapCoords) => {
    mapRef.current?.flyTo({
      center: [coords.longitude, coords.latitude],
      zoom: PICK_ZOOM,
      duration: 700,
    });
  }, []);

  // Reverse tâm khởi đầu nếu chưa có địa chỉ seed sẵn.
  const handleLoad = useCallback(() => {
    if (!initialAddress) {
      void reverse(center.latitude, center.longitude);
    }
  }, [initialAddress, reverse, center]);

  const handleUseGps = useCallback(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        flyTo({
          longitude: pos.coords.longitude,
          latitude: pos.coords.latitude,
        });
      },
      (err) => console.error('❌ GPS error:', err),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }, [flyTo]);

  const handleConfirm = useCallback(async () => {
    if (!address) return;
    setConfirming(true);
    try {
      await onConfirm(address);
      onOpenChange(false);
    } catch (error) {
      console.error('❌ MapPicker confirm failed:', error);
    } finally {
      setConfirming(false);
    }
  }, [address, onConfirm, onOpenChange]);

  const canConfirm = Boolean(address) && !isMoving && !isLoading && !confirming;
  const busy = isMoving || isLoading;

  return (
    <div className="relative flex size-full flex-col overflow-hidden bg-muted">
      {/* Map + overlays */}
      <div className="relative flex-1">
        {mounted ? (
          <Suspense fallback={<MapSkeleton />}>
            <LazyMapView
              ref={mapRef}
              id="home-address-picker"
              initialCenter={center}
              initialZoom={PICK_ZOOM}
              onLoad={handleLoad}
              onMoveStart={onMoveStart}
              onMoveEnd={onMoveEnd}
            />
          </Suspense>
        ) : (
          <MapSkeleton />
        )}

        {/* Pin cố định giữa */}
        <CenterPin isMoving={isMoving} />

        {/* Nút back góc trên-trái */}
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="absolute left-4 top-4 z-20 flex size-10 items-center justify-center rounded-full bg-white text-foreground shadow-lg shadow-black/10 transition-transform active:scale-95"
          aria-label="Quay lại"
        >
          <ChevronLeft className="size-5" />
        </button>

        {/* Ô search nổi */}
        <div className="absolute inset-x-4 top-4 z-10 ml-12">
          <MapAddressSearch
            biasCenter={{ lat: center.latitude, lng: center.longitude }}
            onPick={flyTo}
          />
        </div>

        {/* FAB về vị trí GPS */}
        <button
          type="button"
          onClick={handleUseGps}
          className="absolute bottom-4 right-4 z-10 flex size-11 items-center justify-center rounded-full bg-white text-primary shadow-lg shadow-black/10 transition-transform active:scale-95"
          aria-label="Về vị trí của tôi"
        >
          <LocateFixed className="size-5" />
        </button>
      </div>

      {/* Bottom card: địa chỉ + xác nhận */}
      <motion.div
        initial={{ y: 24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 320, damping: 32 }}
        className="z-10 rounded-t-2xl bg-white px-4 pb-6 pt-4 shadow-[0_-8px_24px_rgba(0,0,0,0.08)]"
      >
        <div className="mb-4 flex items-start gap-3">
          <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <MapPin className="size-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-muted-foreground">
              Giao đến địa chỉ này
            </p>
            <AnimatePresence mode="wait" initial={false}>
              {busy ? (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="mt-1 space-y-1.5"
                >
                  <span className="block h-3.5 w-2/3 animate-pulse rounded bg-muted" />
                  {/* <span className="block text-xs text-muted-foreground">
                    Đang tìm địa chỉ...
                  </span> */}
                </motion.div>
              ) : (
                <motion.div
                  key="address"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                >
                  <p className="truncate text-sm font-semibold text-foreground">
                    {address?.short_address ||
                      address?.address ||
                      'Kéo bản đồ để chọn vị trí'}
                  </p>
                  {address?.address &&
                    address.address !== address.short_address && (
                      <p className="truncate text-xs text-muted-foreground">
                        {address.address}
                      </p>
                    )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <Button
          type="button"
          size="lg"
          className="h-12 w-full rounded-xl text-base font-semibold"
          disabled={!canConfirm}
          onClick={() => void handleConfirm()}
        >
          {confirming ? 'Đang xử lý...' : 'Xác nhận vị trí này'}
        </Button>
      </motion.div>
    </div>
  );
}

export default function MapPickerSheet({
  open,
  onOpenChange,
  ...rest
}: MapPickerSheetProps) {
  const isMobile = useIsMobile();

  if (!open) return null;

  const content = <MapPickerContent onOpenChange={onOpenChange} {...rest} />;

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          className={cn(
            'mx-auto flex size-full w-full max-w-lg flex-col gap-0 p-0',
            '[&>button]:hidden'
          )}
        >
          <SheetTitle className="sr-only">
            {rest.title ?? 'Chọn vị trí trên bản đồ'}
          </SheetTitle>
          {content}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[80vh] max-w-2xl flex-col gap-0 overflow-hidden p-0">
        <DialogTitle className="sr-only">
          {rest.title ?? 'Chọn vị trí trên bản đồ'}
        </DialogTitle>
        {content}
      </DialogContent>
    </Dialog>
  );
}
